/**
 * useRunChat — unified chat hook for Rate Quoting v2.
 *
 * Posts to POST /api/ams/rate-quoting/runs/{runId}/chat for both
 * review-gate and completed-run modes; the server resolves context.
 *
 * Returned `send()` delivers the raw response so callers can handle
 * mode-specific fields (updated_review, action, requote_mods, etc.).
 *
 * `localTransact()` handles non-AI interactions (field-input submit)
 * while keeping all messages in the same thread. It also persists
 * those messages to MongoDB via POST /save-messages so they appear
 * in View Details history alongside AI chat turns.
 */
import React, { useCallback, useRef, useState } from 'react';

export interface PendingAction {
  action_id:   string;
  action_type: string;
  params:      Record<string, any>;
  summary:     string;
}

export interface RunChatMessage {
  id:            number;
  role:          'user' | 'ai';
  text:          string;
  pendingAction?: PendingAction;
}

export interface ConfirmOpts {
  confirm_action_id: string;
  action_type:       string;
  action_params:     Record<string, any>;
}

export interface UseRunChatResult {
  messages:      RunChatMessage[];
  thinking:      boolean;
  /** Synchronous ref to the messages array — reads the latest value even inside
   *  useEffect closures, avoiding stale React state race conditions. Use
   *  messagesRef.current.length instead of messages.length when setting
   *  quoteSeedIdxRef inside effects that react to SSE status changes. */
  messagesRef:   React.MutableRefObject<RunChatMessage[]>;
  /** POST to unified /chat endpoint; returns raw response data or null on error. */
  send:          (text: string, confirmOpts?: ConfirmOpts) => Promise<Record<string, any> | null>;
  /** Run a non-AI side-effect (e.g. submitStepInput) while showing user/AI bubbles.
   *  Messages are persisted to MongoDB so they appear in View Details history. */
  localTransact: (
    userText:     string,
    work:         () => Promise<void>,
    successReply: string,
    errorReply:   string,
  ) => Promise<void>;
  /** Remove the pendingAction chip from a specific message (Dismiss). */
  dismiss:       (actionId: string) => void;
  /** Append a single AI bubble without any fetch (seeding). */
  append:        (text: string) => void;
  /** Replace all messages with a single seeded AI bubble. */
  seed:          (text: string) => void;
  /** Clear all messages and history. */
  reset:         () => void;
  /** Restore a full saved conversation (from MongoDB). Replaces all messages. */
  restore:       (msgs: Array<{ role: 'user' | 'ai'; text: string; id: number }>) => void;
  /** Persist the current messages with the pre-quote count so view-details can
   *  restore Q&A bubbles ABOVE the quote cards in the correct order.
   *  Call this just before appending the quote seed bubble. */
  saveQuoteSeedIdx: (preQuoteCount: number) => void;
}

export function useRunChat(backendRunId: string | null): UseRunChatResult {
  const [messages, setMessages] = useState<RunChatMessage[]>([]);
  const [thinking, setThinking]  = useState(false);
  const historyRef  = useRef<Array<{ role: string; content: string }>>([]);
  // Mirrors messages state synchronously so callbacks can read latest value
  // without waiting for a re-render (needed in localTransact's persist call).
  const messagesRef = useRef<RunChatMessage[]>([]);

  /** Update both state and the synchronous ref in one call.
   *  The ref is updated IMMEDIATELY (synchronously) so that any code running
   *  after _setMessages can read messagesRef.current without waiting for React
   *  to re-render. setMessages receives the computed value (not an updater)
   *  which is fine because all our calls are sequential, never concurrent. */
  const _setMessages = useCallback((updater: (prev: RunChatMessage[]) => RunChatMessage[]) => {
    const next = updater(messagesRef.current);
    messagesRef.current = next;
    setMessages(next);
  }, []);

  /** Fire-and-forget: send current messages to MongoDB /save-messages.
   *  Only used after non-AI events (localTransact). AI chat is persisted
   *  server-side after every /chat response.
   *  @param preQuoteCount  When provided, saves the number of messages that
   *    existed BEFORE quotes arrived so view-details can restore them above
   *    the quote cards in the correct chronological order. */
  const _persist = useCallback((msgs: RunChatMessage[], preQuoteCount?: number) => {
    if (!backendRunId) return;
    const authToken = localStorage.getItem('authToken') || '';
    const payload = msgs
      .filter(m => !m.pendingAction)          // skip confirmation chips
      .map(m => ({ id: m.id, role: m.role, text: m.text }));
    const body: Record<string, any> = { messages: payload };
    if (preQuoteCount !== undefined) body.pre_quote_count = preQuoteCount;
    fetch(
      `${process.env.REACT_APP_MyAMP_Url}/api/ams/rate-quoting/runs/${backendRunId}/save-messages`,
      {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body:    JSON.stringify(body),
      },
    ).catch(() => { /* silent — non-critical */ });
  }, [backendRunId]);

  const send = useCallback(async (
    text:        string,
    confirmOpts?: ConfirmOpts,
  ): Promise<Record<string, any> | null> => {
    if (!text.trim() || thinking || !backendRunId) return null;

    _setMessages(prev => [...prev, { id: Date.now(), role: 'user', text: text.trim() }]);
    setThinking(true);
    historyRef.current.push({ role: 'user', content: text.trim() });

    try {
      const authToken = localStorage.getItem('authToken') || '';
      const body: Record<string, any> = {
        message: text.trim(),
        history: historyRef.current.slice(0, -1),
      };
      if (confirmOpts) {
        body.confirm_action_id = confirmOpts.confirm_action_id;
        body.action_type       = confirmOpts.action_type;
        body.action_params     = confirmOpts.action_params;
      }

      const resp = await fetch(
        `${process.env.REACT_APP_MyAMP_Url}/api/ams/rate-quoting/runs/${backendRunId}/chat`,
        {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body:    JSON.stringify(body),
        },
      );

      if (resp.ok) {
        const data = await resp.json();
        const reply: string = data.reply || 'Done.';
        const aiMsg: RunChatMessage = { id: Date.now() + 1, role: 'ai', text: reply };

        if (data.pending_confirmation) {
          aiMsg.pendingAction = {
            action_id:   data.pending_confirmation.action_id   ?? `pa-${Date.now()}`,
            action_type: data.pending_confirmation.action_type ?? '',
            params:      data.pending_confirmation.params      ?? {},
            summary:     data.pending_confirmation.summary     ?? '',
          };
        }

        _setMessages(prev => [...prev, aiMsg]);
        historyRef.current.push({ role: 'assistant', content: reply });
        return data;
      } else {
        _setMessages(prev => [
          ...prev,
          { id: Date.now() + 1, role: 'ai', text: "I couldn't process that — please try again." },
        ]);
        historyRef.current.pop();
        return null;
      }
    } catch {
      _setMessages(prev => [
        ...prev,
        { id: Date.now() + 1, role: 'ai', text: 'Connection issue. Please try again.' },
      ]);
      historyRef.current.pop();
      return null;
    } finally {
      setThinking(false);
    }
  }, [backendRunId, thinking, _setMessages]);

  const localTransact = useCallback(async (
    userText:     string,
    work:         () => Promise<void>,
    successReply: string,
    errorReply:   string,
  ) => {
    // Append user bubble + "Got it" OPTIMISTICALLY and synchronously before
    // the HTTP call so messagesRef already contains both bubbles by the time
    // any SSE event (e.g. "completed") can arrive.  This eliminates the race
    // where the SSE fires before the HTTP response and the quote-seed effect
    // captures preQuoteCount before "Got it" is in the array.
    const replyId = Date.now() + 1;
    _setMessages(prev => [
      ...prev,
      { id: Date.now(),  role: 'user', text: userText },
      { id: replyId,     role: 'ai',   text: successReply },
    ]);
    setThinking(true);
    try {
      await work();
      // Success — "Got it" is already shown; nothing more to append.
    } catch {
      // Swap "Got it" for the error message in-place.
      _setMessages(prev => prev.map(m =>
        m.id === replyId ? { ...m, text: errorReply } : m,
      ));
    } finally {
      setThinking(false);
      // Persist both bubbles so View Details shows them.
      _persist(messagesRef.current);
    }
  }, [_setMessages, _persist]);

  const dismiss = useCallback((actionId: string) => {
    _setMessages(prev => prev.map(m =>
      m.pendingAction?.action_id === actionId ? { ...m, pendingAction: undefined } : m,
    ));
  }, [_setMessages]);

  const append = useCallback((text: string) => {
    _setMessages(prev => [...prev, { id: Date.now(), role: 'ai', text }]);
  }, [_setMessages]);

  const seed = useCallback((text: string) => {
    const msg = [{ id: 0, role: 'ai' as const, text }];
    messagesRef.current = msg;
    setMessages(msg);
  }, []);

  const reset = useCallback(() => {
    messagesRef.current = [];
    setMessages([]);
    setThinking(false);
    historyRef.current = [];
  }, []);

  const restore = useCallback((msgs: Array<{ role: 'user' | 'ai'; text: string; id: number }>) => {
    const mapped = msgs.map(m => ({ id: m.id, role: m.role, text: m.text }));
    messagesRef.current = mapped;
    setMessages(mapped);
    historyRef.current = msgs.map(m => ({
      role: m.role === 'ai' ? 'assistant' : 'user',
      content: m.text,
    }));
  }, []);

  const saveQuoteSeedIdx = useCallback((preQuoteCount: number) => {
    _persist(messagesRef.current, preQuoteCount);
  }, [_persist]);

  return { messages, thinking, messagesRef, send, localTransact, dismiss, append, seed, reset, restore, saveQuoteSeedIdx };
}

